// PixiJS compiles its shader and uniform upload paths with `new Function`, which
// a Content-Security-Policy without 'unsafe-eval' blocks -- and the game is
// served under exactly such a policy. This side-effect import swaps those paths
// for interpreted equivalents, which is Pixi's supported way to run under a
// strict CSP. It must come before anything touches the renderer, so it is the
// first import in the entry module.
import 'pixi.js/unsafe-eval';

import {
  LANGUAGES,
  applyToDocument,
  currentLanguage,
  detectLanguage,
  onLanguageChange,
  setLanguage,
  storeLanguage,
  t,
} from './i18n';
import {
  ApiError,
  api,
  isOperatorMode,
  launchContext,
  storedToken,
  storeLaunchContext,
  storeToken,
} from './net/api';
import { GameSocket, TicketRefused, type SocketStatus } from './net/socket';
import {
  ErrorCode,
  SLOTS,
  ServerMessage,
  type BetAccepted,
  type BetCanceled,
  type SlotBudget,
  type BetCashoutFeed,
  type BetPlacedFeed,
  type CashedOut,
  type ProtocolError,
  type RoundBetting,
  type RoundCrash,
  type RoundStart,
  type RoundTick,
  type SessionResponse,
  type Slot,
  type RoundResult,
  type Snapshot,
  type SnapshotBet,
  type WalletUpdate,
} from './net/protocol';
import { ClientCurve } from './game/curve';
import { GameStage } from './scene/stage';
import { AutoBet } from './ui/autobet';
import { BetPanels } from './ui/betpanel';
import { Countdown } from './ui/countdown';
import { type Commitments, FairPanel } from './ui/fairpanel';
import { Feed } from './ui/feed';
import { Gate } from './ui/gate';
import { Hud, band } from './ui/hud';
import { Menu } from './ui/menu';
import { AvatarPanel, LimitsPanel } from './ui/profilepanel';
import { avatarSvg } from './ui/avatars';
import { applyServerAudio, audioState, setAudio } from './audio/settings';
import { armAudio, loadAudio, play as playSound } from './audio/player';
import { localise, localiseBlocked } from './ui/messages';
import { Toasts } from './ui/toast';
import { formatMoney, formatMoneyExact, formatMultiplier } from './util/format';

/** One of our own stakes, as the server last described it. */
interface OwnBet {
  betId: string;
  roundId: string;
  amount: string;
  settled: boolean;
  cashedOutX100?: number;
  /**
   * The auto cash-out target the server is holding for this stake, if any.
   *
   * Kept because a reconnect has to show it: the field is otherwise empty while
   * the server is still going to settle the bet at that multiplier, which reads
   * as "my target was lost".
   */
  autoCashoutX100?: number;
}

/**
 * The application controller.
 *
 * It holds no game truth of its own. Every value it displays arrived from the
 * server, and every action it takes is a request. Two things are computed
 * locally, both rendering details: the multiplier drawn between server ticks
 * (corrected by every tick that arrives), and which round a pre-bet has moved
 * into -- and that is a comparison of two round ids the server sent us, not a
 * decision.
 */
class App {
  private readonly stage = new GameStage();
  private readonly curve = new ClientCurve();
  private readonly toasts = new Toasts(must('toast'));
  private readonly hud = new Hud();
  private readonly menu = new Menu();
  private readonly countdown = new Countdown();
  private readonly fair = new FairPanel(this.menu, {
    commitments: () => this.commitments,
    pool: () => this.poolCode,
    lastResult: () => this.lastResult,
  });
  private readonly avatarPanel = new AvatarPanel(this.menu, {
    current: () => this.avatarId,
    onChosen: (id) => {
      this.avatarId = id;
      this.renderMenuAvatar();
    },
    notify: (message, kind) => this.toasts.show(message, kind),
  });
  private readonly limitsPanel = new LimitsPanel(this.menu, {
    limits: () => this.session?.limits ?? null,
    currency: () => this.session?.wallet.currency ?? '',
    scale: () => this.scale,
    symbol: () => this.symbol,
  });
  private readonly langSelect = select('lang');

  private feed!: Feed;
  private panels!: BetPanels;
  private autoBet!: AutoBet;
  private socket: GameSocket | null = null;
  private readonly gate: Gate;

  private session: SessionResponse | null = null;
  /** The fun-mode block from the handshake, or null for a real-money session. */
  private demo: { bankroll: string } | null = null;
  private demoTopUpItem: HTMLButtonElement | null = null;
  private scale = 2;
  private symbol = '';

  /**
   * Our stakes in the round on screen, and our stakes on the round after it,
   * both keyed by slot. Separate maps rather than a flag on one: they are
   * cancelled under different rules and displayed differently, and a pre-bet
   * moves from one to the other when its round opens.
   */
  private bets = new Map<Slot, OwnBet>();
  private preBets = new Map<Slot, OwnBet>();

  private currentRoundId = '';
  private nextRoundId = '';

  /**
   * How many times each slot may still be taken back, by round id.
   *
   * Keyed by round rather than by slot alone because a stake offered during a
   * running round is for the *next* round, and that round has its own allowance
   * -- a pre-bet may be cancelled at any time, so it is the one a player can
   * spend before the round they are spending it on has even opened.
   *
   * The server is still the authority: it refuses the stake either way
   * (`cancel_limit_reached`). This is only what the button is allowed to invite,
   * and every figure in here came from the server -- seeded by the snapshot,
   * moved by `bet.canceled`, and started at the full allowance for a round that
   * has only just been sealed.
   */
  private readonly cancelsLeft = new Map<string, Map<Slot, number>>();
  private maxCancelsPerSlot = 0;
  private phase: 'betting' | 'running' | 'crashed' = 'crashed';
  private blocked = false;

  /**
   * The published commitments and the last round we watched settle.
   *
   * Kept here rather than read back out of the HUD: the fairness page needs the
   * same four values the strip under the canvas shows, and the HUD is a view of
   * them, not their owner.
   */
  private commitments: Commitments | null = null;
  private lastResult: RoundResult | null = null;

  /**
   * The round series this session plays in, from the snapshot.
   *
   * Every fairness lookup carries it, because a round number is counted inside
   * one series. Empty until the first snapshot arrives, which the server reads as
   * the shared series -- the public game, and the right answer for a player
   * without an operator rate of their own.
   */
  private poolCode = '';

  /** The avatar the server holds for us, resolved (never "unchosen"). */
  private avatarId = 0;

  /** The two audio switches in the drawer, kept so the server's answer can move them. */
  private audioSwitches: Partial<Record<'sound' | 'music', HTMLButtonElement>> = {};

  /** The phase caption on the canvas, kept so a language change can redraw it. */
  private stageCaption: (() => void) | null = null;

  private renderLoopStarted = false;

  constructor() {
    this.gate = new Gate({
      onAuthenticated: () => void this.openGame(),
      onLanguagePicked: (tag) => this.changeLanguage(tag, { redrawGate: true }),
    });
  }

  async boot(): Promise<void> {
    // The language comes first: everything below draws text, and drawing it
    // twice -- once in English, once in the player's language -- is a visible
    // flash on a slow connection.
    await setLanguage(detectLanguage());
    this.buildLanguagePicker();
    onLanguageChange(() => this.onLanguageChanged());

    await this.stage.mount(must('stage'));

    this.panels = new BetPanels({
      onPlace: (slot, amount, auto, nextRound) => this.placeBet(slot, amount, auto, nextRound),
      onCancel: (slot, nextRound) => this.cancelBet(slot, nextRound),
      onCashOut: (slot) => this.cashOut(slot),
      onAutoBetChange: (slot, on) => this.onAutoBetChange(slot, on),
    });

    this.autoBet = new AutoBet({
      place: (slot, nextRound) => this.panels.panelFor(slot).autoPlace(nextRound),
      turnOff: (slot) => this.panels.panelFor(slot).setAutoBet(false),
      notifyStopped: () => this.toasts.show(t('toast.autoBetOff'), 'bad'),
    });

    must('btn-logout').addEventListener('click', async () => {
      await api.logout();
      location.reload();
    });

    must('btn-limits').addEventListener('click', () => {
      const limits = this.session?.limits;
      // The card is a gate overlay, which draws above the drawer. Closing the
      // drawer first means one dialog on screen instead of two stacked.
      this.menu.close();
      this.gate.showLimits(
        {
          dailyStakeMax: limits?.dailyStakeMax,
          weeklyStakeMax: limits?.weeklyStakeMax,
        },
        () => void this.refreshSession(),
      );
    });

    must('btn-chat').addEventListener('click', () => this.showChatPage());
    must('history-more').addEventListener('click', () => this.showHistoryPage());

    this.buildMenu();

    if (isOperatorMode()) {
      await this.bootFromLaunch();
    } else if (storedToken()) {
      await this.openGame();
    } else {
      await this.gate.showLogin();
    }
  }

  /**
   * Opens the game from an operator's launch URL: /play?session=<token>.
   *
   * A token in the URL wins over the stored session. The same browser can hold a
   * session from another casino -- the player left one site's Fighter and opened
   * it from a different one -- and the stored session would then show the old
   * casino's account and balance inside the new casino's frame. The URL is the
   * only thing that says which launch this frame belongs to.
   *
   * The stored session is used when there is no token (the URL was already
   * cleaned up by an earlier load), and when the token turns out to be spent: the
   * launch token is single-use, so a second frame on the same page, or an iframe
   * the casino re-renders with the same src, would otherwise be met with
   * "expired" while the session that token produced sat in storage.
   *
   * There is no sign-in screen to fall back to here. Nobody inside a casino's
   * iframe has a Fighter password, so a failure says what happened and asks them
   * to reopen the game from the casino, which is the only thing that can help.
   */
  private async bootFromLaunch(): Promise<void> {
    const token = new URLSearchParams(location.search).get('session');
    if (!token) {
      if (storedToken()) {
        try {
          await this.openGame({ launchMode: true });
          return;
        } catch {
          // Fall through: the stored session was not usable and there is no token.
        }
      }
      this.gate.showBlocked(t('app.launchMissing'), { hideAccountActions: true });
      return;
    }

    try {
      const session = await api.b2bSession(token);
      storeToken(session.token);
      // Before the reload that a refresh will be: the launch token cannot be
      // redeemed twice, so this is the only moment the lobby link exists.
      storeLaunchContext({
        ...(session.returnUrl ? { returnUrl: session.returnUrl } : {}),
        ...(session.platform ? { platform: session.platform } : {}),
      });
    } catch (e) {
      // A spent token: the session it produced is the one in storage, if any.
      if (e instanceof ApiError && e.code === ErrorCode.LaunchExpired && storedToken()) {
        history.replaceState(null, '', location.pathname);
        try {
          await this.openGame({ launchMode: true });
          return;
        } catch {
          // Fall through to the expired-link message.
        }
      }
      const message =
        e instanceof ApiError ? localise(e.code, e.message) : t('app.serverUnreachable');
      this.gate.showBlocked(message, { hideAccountActions: true });
      return;
    }

    // The token is spent. Taking it out of the URL means a reload cannot present
    // a dead one, and keeps it out of the history entry and any referrer.
    history.replaceState(null, '', location.pathname);
    await this.openGame({ launchMode: true });
  }

  // ------------------------------------------------------------------------
  // The drawer
  // ------------------------------------------------------------------------

  /**
   * Fills in the drawer around the three rows that are static markup.
   *
   * Each entry says where it belongs rather than relying on the order modules
   * happen to load in: the fairness and avatar pages go above the language row,
   * the audio switches and the operator limits below it, and sign-out stays last.
   *
   * "Game limits" and "Responsible gambling" sit next to each other and are
   * different things on purpose -- the first is the operator's, read-only; the
   * second is the player's own and editable -- so their labels have to carry that
   * distinction. They are the two rows most likely to be confused for each other.
   */
  private buildMenu(): void {
    this.menu.addItem({
      id: 'mi-fair',
      labelKey: 'menu.fair',
      before: 'menu-lang-row',
      onClick: () => this.fair.show(),
    });
    this.menu.addItem({
      id: 'mi-avatar',
      labelKey: 'menu.avatar',
      before: 'menu-lang-row',
      onClick: () => this.avatarPanel.show(),
    });

    this.menu.addRow({
      labelKey: 'sound.effects',
      control: this.audioSwitch('sound'),
      before: 'btn-limits',
    });
    this.menu.addRow({
      labelKey: 'sound.music',
      control: this.audioSwitch('music'),
      before: 'btn-limits',
    });
    this.menu.addNote('sound.notYet', 'btn-limits');
    this.menu.addItem({
      id: 'mi-game-limits',
      labelKey: 'menu.gameLimits',
      before: 'btn-limits',
      onClick: () => this.limitsPanel.show(),
    });

    // Built once and hidden, like everything else in the drawer: the handshake
    // decides whether it is shown, and the handshake happens after this.
    this.demoTopUpItem = this.menu.addItem({
      id: 'mi-demo-topup',
      labelKey: 'demo.reset',
      before: 'btn-limits',
      onClick: () => void this.topUpDemo(),
    });
    this.demoTopUpItem.hidden = true;

    this.renderMenuAvatar();
    this.syncAudioSwitches();
  }

  /**
   * One audio switch.
   *
   * The switch moves first and is put back if the server refuses, which is the
   * opposite of the avatar chooser: a mute that waits for a round trip feels
   * broken, while an avatar that appears to save and did not is a lie about how
   * other players see you.
   */
  private audioSwitch(kind: 'sound' | 'music'): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'switch';
    button.setAttribute('role', 'switch');
    button.setAttribute('aria-checked', 'false');

    button.addEventListener('click', () => {
      const next = button.getAttribute('aria-checked') !== 'true';
      button.setAttribute('aria-checked', next ? 'true' : 'false');
      void setAudio({ [kind]: next }).catch(() => {
        this.syncAudioSwitches();
        this.toasts.show(t('sound.failed'), 'bad');
      });
    });

    this.audioSwitches[kind] = button;
    return button;
  }

  private syncAudioSwitches(): void {
    const state = audioState();
    this.audioSwitches.sound?.setAttribute('aria-checked', state.sound ? 'true' : 'false');
    this.audioSwitches.music?.setAttribute('aria-checked', state.music ? 'true' : 'false');
  }

  private renderMenuAvatar(): void {
    const host = must('menu-avatar');
    host.replaceChildren(avatarSvg(this.avatarId));
  }

  // ------------------------------------------------------------------------
  // Language
  // ------------------------------------------------------------------------

  private buildLanguagePicker(): void {
    for (const l of LANGUAGES) {
      const option = document.createElement('option');
      option.value = l.tag;
      // Each language under its own name: a player who has landed on one they
      // cannot read still recognises their own.
      option.textContent = l.name;
      this.langSelect.appendChild(option);
    }
    this.langSelect.value = currentLanguage();
    this.langSelect.addEventListener('change', () => {
      void this.changeLanguage(this.langSelect.value, { redrawGate: true });
    });
  }

  /**
   * Applies a language, remembers it on this device, and -- when there is a
   * session -- stores it on the account so the next device agrees.
   *
   * The interface is switched before the request is sent, not after: a language
   * change should not appear to hang because the network is slow, and the server
   * copy exists for the next sign-in, not for this frame.
   */
  private async changeLanguage(tag: string, opts: { redrawGate?: boolean } = {}): Promise<void> {
    if (tag === currentLanguage()) return;

    await setLanguage(tag);
    storeLanguage(tag);
    this.langSelect.value = currentLanguage();

    if (opts.redrawGate && this.gate.isOpen) this.gate.redraw();

    if (this.session) {
      try {
        await api.setLanguage(currentLanguage());
      } catch (e) {
        // Not fatal: the choice is applied and remembered locally either way.
        if (e instanceof ApiError) this.toasts.show(localise(e.code, e.message), 'bad');
      }
    }
  }

  /** Redraws everything whose text this controller owns. */
  private onLanguageChanged(): void {
    applyToDocument();
    this.langSelect.value = currentLanguage();
    this.stageCaption?.();
    // The panels exist from partway through boot; a change before that has
    // nothing to redraw, and the first render will use the new language anyway.
    if (this.panels) this.renderSlots();
  }

  // ------------------------------------------------------------------------
  // Handshake
  // ------------------------------------------------------------------------

  /**
   * Puts away the parts of the interface that are not ours to offer.
   *
   * Inside an operator's page the account belongs to that operator: signing out
   * of Fighter would strand the player in an iframe with no way back in, and our
   * responsible-gambling controls are not the ones their licence runs on. What
   * stays is everything about the game itself -- fairness, avatar, language,
   * sound, the game's own limits -- because those are ours either way.
   */
  private applyMode(session: SessionResponse): void {
    const operatorMode = session.mode === 'operator';
    for (const id of ['btn-logout', 'btn-limits']) {
      const el = document.getElementById(id);
      if (el) el.hidden = operatorMode;
    }
    if (operatorMode && session.operator) {
      // The casino's name, where the player's own nickname goes on our own site:
      // in someone else's page, "whose game is this" is the more useful fact, and
      // the nickname is already in the feed.
      const nick = document.getElementById('nickname');
      if (nick) nick.textContent = session.operator.name;
    }

    // Fun mode. The badge is not decoration: everything else on the screen -- the
    // balance, the win toast, the feed -- reads exactly as it does for real money,
    // so the one thing that must be unmistakable is that this money is not.
    this.demo = session.demo ?? null;
    const badge = document.getElementById('demo-badge');
    if (badge) badge.hidden = !this.demo;
    if (this.demoTopUpItem) this.demoTopUpItem.hidden = !this.demo;

    this.applyLobbyLink();
    this.applyPlatform();
  }

  /**
   * Shows the way back to the casino, if the operator sent one.
   *
   * The href is set from the stored launch context rather than from anything in
   * the page, and only ever to what the server handed us: the launch endpoint
   * refuses a returnUrl that is not absolute HTTPS, so nothing here can become a
   * javascript: link.
   */
  private applyLobbyLink(): void {
    const link = document.getElementById('btn-lobby');
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = launchContext().returnUrl;
    if (!url) {
      link.hidden = true;
      link.removeAttribute('href');
      return;
    }
    link.href = url;
    link.hidden = false;
  }

  /**
   * Records the operator's own answer to "what is this player on".
   *
   * It does not pick the layout, and that is deliberate: the layout follows the
   * *box* the game was given -- a 420px column on a desktop casino page wants the
   * narrow layout as much as a phone does, and the media queries already see
   * that. A device label would be the worse of the two signals.
   *
   * What it is good for is everything around the layout: the value is on the
   * document, so a per-operator stylesheet has a hook, and it is in our launch
   * log next to the player id, so a support ticket about a phone can be checked
   * against what the casino said it was.
   */
  private applyPlatform(): void {
    const platform = launchContext().platform;
    if (platform) document.documentElement.dataset['platform'] = platform;
    else delete document.documentElement.dataset['platform'];
  }

  /** Refills the play balance and says so. Only reachable in fun mode. */
  private async topUpDemo(): Promise<void> {
    try {
      const { balance } = await api.demoTopUp();
      this.hud.setBalance(balance);
      this.toasts.show(
        t('toast.demoReset', { amount: formatMoney(balance, this.scale, this.symbol) }),
        'win',
      );
    } catch (e) {
      if (e instanceof ApiError) this.toasts.show(localise(e.code, e.message), 'bad');
      else this.toasts.show(t('app.serverUnreachable'), 'bad');
    }
  }

  /**
   * Performs the handshake and, only if it succeeds, opens the game.
   *
   * Nothing about the game screen is set up before this returns: the currency,
   * the scale, the limits, the language and the right to bet all come from here.
   */
  private async openGame(opts: { launchMode?: boolean } = {}): Promise<void> {
    let session: SessionResponse;
    try {
      session = await api.session();
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401) {
          storeToken(null);
          // Inside a casino's iframe there is no sign-in screen to offer: the
          // caller is trying a stored session before spending the launch token,
          // and this is how it learns the session was no good.
          if (opts.launchMode) throw e;
          await this.gate.showLogin(localise(e.code, e.message));
          return;
        }
        // A 403 carries the reason the account may not play.
        this.showBlockedByAccount(e);
        return;
      }
      await this.gate.showLogin(t('app.serverUnreachable'));
      return;
    }

    this.session = session;
    this.blocked = false;
    this.scale = session.wallet.scale;
    this.symbol = session.wallet.symbol;

    // The server resolved the language: a preference stored on the account beats
    // whatever this browser guessed before signing in.
    if (session.locale.language !== currentLanguage()) {
      await setLanguage(session.locale.language);
      this.langSelect.value = currentLanguage();
    }

    // The account is the source of truth for both of these; what the browser
    // remembered was only there to draw the first frame.
    this.avatarId = session.player.avatarId;
    this.renderMenuAvatar();
    applyServerAudio(session.preferences.sound, session.preferences.music);
    this.syncAudioSwitches();

    // The event-to-file mapping the back office wrote, and the gesture gate the
    // browser insists on before any audio may start. Neither blocks the
    // handshake: a manifest that never arrives means silence, not a game that
    // will not open.
    void loadAudio();
    armAudio();

    this.feed = new Feed(this.scale);
    this.hud.setPlayer(session.player.nickname, session.wallet.currency, this.scale, this.symbol);
    // After the HUD, not before: setPlayer writes the player line that operator
    // mode then replaces with the casino's name.
    this.applyMode(session);
    this.hud.setBalance(session.wallet.balance);
    this.hud.setLimitsHint(
      session.limits.minBet,
      session.limits.maxBet,
      session.limits.dailyStakeMax,
      session.limits.dailyStakeUsed,
    );
    const history = session.game.history ?? [];
    this.hud.setHistory(history, (nonce) => this.verifyRound(nonce));
    // The newest settled round, so the fairness page has something to check
    // before this client has watched a round of its own.
    this.lastResult = history[0] ?? null;

    this.panels.configure({
      scale: this.scale,
      minBet: session.limits.minBet,
      maxBet: session.limits.maxBet,
      symbol: this.symbol,
      // Per account, so two players sharing a browser do not inherit each
      // other's stake or their second panel.
      playerKey: session.player.id,
    });
    this.curve.configure(session.game.doublingSeconds);
    this.feed.reset(session.feed);

    this.applySnapshot(session.game);
    this.gate.hide();

    // The wallet being empty is the normal state of a brand new account, and
    // there is no payment provider yet, so offer the development faucet rather
    // than leaving the player at a dead end.
    if (session.mode === 'player' && session.wallet.balance === '0' && this.looksLikeDevelopment()) {
      this.gate.showDevFaucet((balance) => {
        this.hud.setBalance(balance);
        this.toasts.show(t('toast.testBalance'), 'win');
      });
    }

    this.connect();
    this.startRenderLoop();
  }

  /**
   * The card for a handshake refused on account grounds (403).
   *
   * Two paths reach it -- opening the game, and a reconnect asking for a fresh
   * ticket -- and the answer is the same from either: the reason comes off the
   * body, and "retry" means running the whole handshake again, because that is
   * what a lifted ban or a completed KYC needs.
   */
  private showBlockedByAccount(e: ApiError): void {
    const reason = (e.body as { account?: { blockedReason?: string } } | null)?.account
      ?.blockedReason;
    this.blocked = true;
    this.panels.setAll('blocked');
    this.gate.showBlocked(localiseBlocked(reason, localise(e.code, e.message)), {
      allowDevBypass: reason === 'kyc_required' && this.looksLikeDevelopment(),
      onRetry: () => void this.openGame(),
    });
  }

  private async refreshSession(): Promise<void> {
    try {
      const session = await api.session();
      this.session = session;
      this.hud.setBalance(session.wallet.balance);
      this.hud.setLimitsHint(
        session.limits.minBet,
        session.limits.maxBet,
        session.limits.dailyStakeMax,
        session.limits.dailyStakeUsed,
      );
    } catch {
      /* the socket is the primary channel; a failed refresh is not fatal */
    }
  }

  private connect(): void {
    this.socket?.close();
    this.socket = new GameSocket({
      // Each reconnect needs a fresh single-use ticket, which means going back
      // through the handshake -- and that is deliberate: it also re-checks that
      // the account is still allowed to play.
      onNeedsTicket: async () => {
        try {
          const session = await api.session();
          this.session = session;
          this.hud.setBalance(session.wallet.balance);
          return session.realtime;
        } catch (e) {
          // A 401 here means the session the socket belonged to is gone, and no
          // amount of backing off brings it back. Say so and stop: inside a
          // casino's iframe the only thing that helps is reopening the game from
          // the lobby, and on our own site it is signing in again.
          if (e instanceof ApiError && e.status === 401) {
            storeToken(null);
            if (isOperatorMode()) {
              // "Sign in again" is not advice anyone inside a casino's iframe can
              // take: they have no Fighter password. The only thing that helps is
              // the launch, which is what this string already says.
              this.gate.showBlocked(t('err.launch_expired'), { hideAccountActions: true });
            } else {
              void this.gate.showLogin(localise(e.code, e.message));
            }
            throw new TicketRefused(e.message);
          }
          if (e instanceof ApiError && e.status === 403) {
            // The account may not play any more -- banned, self-excluded, KYC
            // due. Backing off changes none of those, so this stops as well; the
            // card's retry button is the way back, and it re-runs the handshake.
            this.showBlockedByAccount(e);
            throw new TicketRefused(e.message);
          }
          throw e;
        }
      },
      onMessage: (type, data) => this.onMessage(type, data),
      onStatus: (status) => this.onStatus(status),
    });
    void this.socket.connect();
  }

  private onStatus(status: SocketStatus): void {
    this.hud.setConnection(status);
    // A stake cannot be taken or settled while the socket is down, and a button
    // that looks live but sends nothing is worse than one that says so.
    if (status !== 'open' && !this.blocked) this.panels.setAll('waiting');

    // Paused rather than stopped: the reconnect brings a snapshot, and that is
    // the moment it is safe to bet again. Unpausing happens there.
    if (status !== 'open') this.autoBet.setPaused(true);
  }

  /**
   * The auto-bet switch was flipped.
   *
   * Turning it on while a round is climbing places a stake on the next round
   * straight away -- the server accepts pre-bets and the player asked for "every
   * round", which starts with the one they can still join.
   */
  private onAutoBetChange(slot: Slot, on: boolean): void {
    this.autoBet.set(slot, on, { placeNow: on && this.phase !== 'betting' });
    if (on) this.toasts.show(t('toast.autoBetOn'));
  }

  // ------------------------------------------------------------------------
  // Server messages
  // ------------------------------------------------------------------------

  private onMessage(type: string, data: unknown): void {
    switch (type) {
      case ServerMessage.Snapshot:
        this.applySnapshot(data as Snapshot);
        break;

      case ServerMessage.RoundBetting: {
        const m = data as RoundBetting;
        this.currentRoundId = m.roundId;
        this.phase = 'betting';

        // Pre-bets for the round that just opened are now stakes in it. The
        // server has already carried them over -- it read them straight from the
        // database when it opened the round -- so this only moves the display to
        // match; it does not place anything. Matching on the round id the server
        // gave us for each is what keeps it honest, and a reconnect replaces the
        // lot with the snapshot's myBets/myNextBets anyway.
        this.bets.clear();
        for (const slot of SLOTS) {
          const pre = this.preBets.get(slot);
          if (pre && pre.roundId === m.roundId) {
            this.bets.set(slot, pre);
            this.preBets.delete(slot);
          }
        }

        // The round that just opened keeps whatever allowance it already had --
        // pre-bet cancels were charged to it before it opened, which is the whole
        // reason the server counts them per round. The round after it has only
        // just been sealed, so its allowance is untouched by definition.
        this.nextRoundId = m.nextRoundId;
        this.seedFullCancels(m.roundId);
        this.seedFullCancels(m.nextRoundId);
        this.pruneCancels();

        this.curve.syncClock(m.serverTime);
        this.curve.stop();

        // The window the server just opened. Drawn from its clock, not ours.
        this.countdown.open(m.bettingEndsAt, m.serverTime);

        this.stage.resetForNextRound();
        this.setStageCaption('betting', () => t('stage.betsOpen'));
        this.feed.reset();
        playSound('round.betting');

        // From the message, not from the handshake: the handshake's value is a
        // round old by now and would show this round's commitment as the next.
        this.setCommitments(m.nonce, m.commitHash, m.nextNonce, m.nextCommitHash);
        this.renderSlots();

        // After renderSlots, never before: the scheduler asks each panel to
        // place a stake and a panel only accepts that in the mode the window
        // being open puts it in.
        this.autoBet.betsOpened();
        break;
      }

      case ServerMessage.RoundStart: {
        const m = data as RoundStart;
        this.currentRoundId = m.roundId;
        this.phase = 'running';
        this.curve.configure(m.doublingSeconds);
        this.curve.syncClock(m.serverTime);
        this.curve.start(m.startedAt);
        // The round has begun because the server says so, which is the only
        // reason the bar is allowed to come down.
        this.countdown.hide();

        this.setStageCaption('running', () => '');
        this.renderSlots();
        playSound('round.start');
        break;
      }

      case ServerMessage.RoundTick: {
        const m = data as RoundTick;
        // A tick for a round we have not seen start -- which can happen for one
        // frame after a reconnect -- would drag the drawn curve backwards.
        if (m.roundId !== this.currentRoundId) break;
        this.curve.acceptServerTick(m.multiplierX100);
        break;
      }

      case ServerMessage.RoundCrash: {
        const m = data as RoundCrash;
        this.phase = 'crashed';
        this.curve.stop();
        this.countdown.hide();
        this.curve.acceptServerTick(m.crashX100);
        playSound('round.crash');

        this.stage.crash(m.crashX100);
        this.setStageCaption('crashed', () =>
          t('stage.burst', { multiplier: formatMultiplier(m.crashX100) }),
        );

        // The seed is public now, so this is a complete, checkable record of the
        // round -- which is what the fairness page verifies in the browser.
        this.lastResult = {
          nonce: m.nonce,
          crashX100: m.crashX100,
          commitHash: m.commitHash,
          serverSeed: m.serverSeed,
          crashedAt: new Date().toISOString(),
          // The RTP this round was sealed with. Carried into the record so that
          // the fairness page re-derives with the round's own value rather than
          // with whatever is configured now.
          rtp: { num: m.rtpNum, den: m.rtpDen },
        };
        this.hud.pushHistory(this.lastResult, (nonce) => this.verifyRound(nonce));

        // One notice however many stakes were lost: two toasts saying the same
        // thing for the same crash is noise.
        const lost = [...this.bets.values()].filter((b) => !b.settled).length;
        if (lost > 0) {
          this.toasts.show(
            t('toast.betLost', { multiplier: formatMultiplier(m.crashX100) }),
            'bad',
          );
        }
        this.renderSlots();
        break;
      }

      case ServerMessage.BetAccepted: {
        const m = data as BetAccepted;
        const slot = asSlot(m.slot);
        if (slot === null) break;

        const bet: OwnBet = {
          betId: m.betId,
          roundId: m.roundId,
          amount: m.amount,
          settled: false,
        };
        if (m.autoCashoutX100) bet.autoCashoutX100 = m.autoCashoutX100;
        if (m.nextRound) {
          this.preBets.set(slot, bet);
          this.toasts.show(t('toast.preBetQueued'));
        } else {
          this.bets.set(slot, bet);
        }

        this.hud.setBalance(m.balance);
        this.renderSlot(slot);
        playSound('bet.placed');
        break;
      }

      case ServerMessage.BetCanceled: {
        const m = data as BetCanceled;
        const slot = asSlot(m.slot);
        if (slot === null) break;

        if (m.nextRound) this.preBets.delete(slot);
        else this.bets.delete(slot);

        // What the server says is left, for the round this stake was in. It
        // arrives on every one of this player's connections, so a cancel on
        // another tab closes the slot here too.
        const budget = this.cancelsLeft.get(m.roundId) ?? new Map<Slot, number>();
        budget.set(slot, m.cancelsLeft);
        this.cancelsLeft.set(m.roundId, budget);

        if (m.walletPending) {
          // A B2B player whose casino has still to take the refund back. The
          // balance is left alone rather than shown as the zero the message
          // carries, and the wallet update that follows is what moves it.
          this.toasts.show(t('toast.betCanceledPending'));
        } else {
          this.hud.setBalance(m.balance);
          this.toasts.show(m.nextRound ? t('toast.preBetCanceled') : t('toast.betCanceled'));
        }
        // A cancellation is always the player's own doing -- nothing in here
        // cancels -- so it is also the answer to "stop betting for me".
        this.autoBet.onManualCancel(slot);
        this.renderSlot(slot);
        break;
      }

      case ServerMessage.CashedOut: {
        const m = data as CashedOut;
        const slot = asSlot(m.slot);
        if (slot === null) break;

        const bet = this.bets.get(slot);
        if (bet) {
          bet.settled = true;
          bet.cashedOutX100 = m.multiplierX100;
        }

        // The exact figure, not the currency's two places: a win below one cent is
        // credited in full and the player is told so.
        const won = formatMoneyExact(m.payout, this.scale, this.symbol);
        if (m.walletPending) {
          // A B2B player: the win is settled here and their casino has still to
          // take it. The balance is deliberately left alone rather than shown
          // stale, and the wallet update that follows is what moves it.
          this.toasts.show(
            t('toast.wonPending', { multiplier: formatMultiplier(m.multiplierX100), amount: won }),
            'win',
          );
        } else {
          this.hud.setBalance(m.balance);
          this.toasts.show(
            t('toast.won', { multiplier: formatMultiplier(m.multiplierX100), amount: won }),
            'win',
          );
        }
        this.renderSlot(slot);
        playSound('bet.cashout');
        break;
      }

      case ServerMessage.WalletUpdate: {
        const m = data as WalletUpdate;
        this.hud.setBalance(m.balance);
        break;
      }

      case ServerMessage.BetPlaced:
        this.feed.onBetPlaced(data as BetPlacedFeed);
        break;

      case ServerMessage.BetCashout:
        this.feed.onCashout(data as BetCashoutFeed);
        break;

      case ServerMessage.Error: {
        const m = data as ProtocolError;
        this.toasts.show(localise(m.code, m.message), 'bad');

        // A refusal that will refuse again stops the automation; the scheduler
        // decides which codes those are.
        this.autoBet.onError(asSlot(m.slot ?? 0), m.code);

        // A block that arrives mid-session (a self-exclusion, a suspension) must
        // close the game, not just show a notice.
        if (m.code === 'betting_blocked') {
          this.blocked = true;
          this.autoBet.stopAll();
          this.panels.setAll('blocked');
          this.gate.showBlocked(localise(m.code, m.message), {
            allowDevBypass: false,
            onRetry: () => void this.openGame(),
          });
          break;
        }

        // Release the button this failure belongs to, and only that one. The
        // slot comes back from the server precisely so the other panel's
        // in-flight request is left alone.
        const slot = asSlot(m.slot ?? 0);
        if (slot !== null) this.renderSlot(slot);
        else this.renderSlots();
        break;
      }

      default:
        break;
    }
  }

  private applySnapshot(snapshot: Snapshot): void {
    this.currentRoundId = snapshot.roundId;
    this.phase = snapshot.phase;
    this.poolCode = snapshot.poolCode;

    this.curve.configure(snapshot.doublingSeconds);
    this.curve.syncClock(snapshot.serverTime);

    this.setCommitments(
      snapshot.nonce,
      snapshot.commitHash,
      snapshot.nextNonce,
      snapshot.nextCommitHash,
    );

    // The snapshot is the whole truth about our stakes, so it replaces both maps
    // rather than merging into them. That is what makes a reconnect mid-round
    // recover a live bet, and a pre-bet placed on another tab appear here.
    this.bets = ownBets(snapshot.myBets, snapshot.roundId);
    this.preBets = ownBets(snapshot.myNextBets, snapshot.nextRoundId);

    // Same rule for the allowance, and the reason a reconnect needs it: cancels
    // made on another tab, or before the socket dropped, are already counted
    // against these rounds in the database and the server sends what is left.
    this.nextRoundId = snapshot.nextRoundId;
    this.maxCancelsPerSlot = snapshot.maxCancelsPerSlot;
    this.cancelsLeft.clear();
    this.seedCancels(snapshot.roundId, snapshot.cancelsLeft);
    this.seedCancels(snapshot.nextRoundId, snapshot.nextCancelsLeft);

    // A stake the server says we hold must have a panel to be cashed out from,
    // even if this browser remembers the second one as closed. Server state wins
    // over the remembered preference.
    for (const slot of SLOTS) {
      const bet = this.bets.get(slot) ?? this.preBets.get(slot);
      if (!bet) continue;
      this.panels.ensureVisible(slot);
      this.panels.panelFor(slot).showServerAutoCashout(bet.autoCashoutX100);
    }

    // State is known again, so the automation may resume. Nothing is placed
    // here: the next betting window is what does that.
    this.autoBet.setPaused(false);

    switch (snapshot.phase) {
      case 'betting':
        this.curve.stop();
        this.stage.resetForNextRound();
        this.setStageCaption('betting', () => t('stage.betsOpen'));
        // A reconnect lands mid-window: the deadline is still the server's, and
        // the bar picks up wherever it honestly is.
        if (snapshot.bettingEndsAt) this.countdown.open(snapshot.bettingEndsAt, snapshot.serverTime);
        else this.countdown.hide();
        break;

      case 'running':
        if (snapshot.startedAt) this.curve.start(snapshot.startedAt);
        this.curve.acceptServerTick(snapshot.multiplierX100);
        this.setStageCaption('running', () => '');
        this.countdown.hide();
        break;

      case 'crashed':
        this.curve.stop();
        this.curve.acceptServerTick(snapshot.multiplierX100);
        this.setStageCaption('crashed', () => t('stage.waiting'));
        this.countdown.hide();
        break;
    }

    this.renderSlots();
  }

  // ------------------------------------------------------------------------
  // Slot state
  // ------------------------------------------------------------------------

  private renderSlots(): void {
    for (const slot of SLOTS) this.renderSlot(slot);
  }

  /** Records the server's figures for one round. */
  private seedCancels(roundId: string, budgets: SlotBudget[] | undefined): void {
    if (!roundId || !budgets) return;
    const perSlot = new Map<Slot, number>();
    for (const b of budgets) {
      const slot = asSlot(b.slot);
      if (slot !== null) perSlot.set(slot, b.left);
    }
    this.cancelsLeft.set(roundId, perSlot);
  }

  /**
   * Gives a round the full allowance, unless something is already known about
   * it.
   *
   * Used for a round the server has just sealed: nobody can have taken a stake
   * back in a round that did not exist, so there is no figure to wait for. The
   * guard matters -- a round we already hold figures for may have had pre-bets
   * cancelled in it, and overwriting those would hand the allowance back.
   */
  private seedFullCancels(roundId: string): void {
    if (!roundId || this.cancelsLeft.has(roundId)) return;
    const perSlot = new Map<Slot, number>();
    for (const slot of SLOTS) perSlot.set(slot, this.maxCancelsPerSlot);
    this.cancelsLeft.set(roundId, perSlot);
  }

  /**
   * True when this slot takes no further stake in this round.
   *
   * Only an explicit zero closes a slot. A round we know nothing about reads as
   * open, which is the safe direction: the server refuses what it must, and
   * greying out a slot the player is entitled to would be the worse mistake.
   */
  private cancelSpent(roundId: string, slot: Slot): boolean {
    return this.cancelsLeft.get(roundId)?.get(slot) === 0;
  }

  /** Two rounds can be bet on; the rest are history. */
  private pruneCancels(): void {
    for (const roundId of [...this.cancelsLeft.keys()]) {
      if (roundId !== this.currentRoundId && roundId !== this.nextRoundId) {
        this.cancelsLeft.delete(roundId);
      }
    }
  }

  /**
   * Derives one slot's display from state alone.
   *
   * Every path that changes anything ends here rather than calling setMode
   * itself. That is what makes an error safe to recover from: the panel's
   * "sending…" lock is cleared by recomputing what the slot really is, so a
   * refused request can never leave a button stuck.
   */
  private renderSlot(slot: Slot): void {
    if (this.blocked) {
      this.panels.setMode(slot, 'blocked');
      return;
    }

    const bet = this.bets.get(slot);
    const pre = this.preBets.get(slot);
    const amount = (b: OwnBet): string => formatMoney(b.amount, this.scale, this.symbol);

    // Which round an empty slot's next stake would go to, and therefore whose
    // allowance decides if it may be offered: this round while the window is
    // open, the next one once it is running.
    switch (this.phase) {
      case 'betting':
        if (bet) this.panels.setMode(slot, 'betPlaced', { amount: amount(bet) });
        else if (pre) this.panels.setMode(slot, 'preBetPlaced', { amount: amount(pre) });
        else if (this.cancelSpent(this.currentRoundId, slot)) {
          this.panels.setMode(slot, 'cancelSpent');
        } else this.panels.setMode(slot, 'canBet');
        return;

      case 'running':
        if (bet && !bet.settled) this.panels.setMode(slot, 'inRound');
        else if (bet) this.panels.setMode(slot, 'cashedOut', { cashedOutX100: bet.cashedOutX100 });
        else if (pre) this.panels.setMode(slot, 'preBetPlaced', { amount: amount(pre) });
        else if (this.cancelSpent(this.nextRoundId, slot)) {
          this.panels.setMode(slot, 'cancelSpent');
        } else this.panels.setMode(slot, 'canPreBet');
        return;

      case 'crashed':
        if (bet && bet.settled) {
          this.panels.setMode(slot, 'cashedOut', { cashedOutX100: bet.cashedOutX100 });
        } else if (bet) this.panels.setMode(slot, 'lost');
        else if (pre) this.panels.setMode(slot, 'preBetPlaced', { amount: amount(pre) });
        else if (this.cancelSpent(this.nextRoundId, slot)) {
          this.panels.setMode(slot, 'cancelSpent');
        } else this.panels.setMode(slot, 'canPreBet');
        return;
    }
  }

  // ------------------------------------------------------------------------
  // Actions and rendering
  // ------------------------------------------------------------------------

  private placeBet(
    slot: Slot,
    amount: string,
    autoCashoutX100: number | undefined,
    nextRound: boolean,
  ): void {
    if (!this.socket?.placeBet(slot, amount, autoCashoutX100, nextRound)) {
      this.toasts.show(t('toast.noConnection'), 'bad');
      this.renderSlot(slot);
    }
  }

  private cancelBet(slot: Slot, nextRound: boolean): void {
    if (!this.socket?.cancelBet(slot, nextRound)) {
      this.toasts.show(t('toast.noConnection'), 'bad');
      this.renderSlot(slot);
    }
  }

  private cashOut(slot: Slot): void {
    if (!this.socket?.cashOut(slot)) {
      this.toasts.show(t('toast.noConnection'), 'bad');
      this.renderSlot(slot);
    }
  }

  private setStageCaption(phase: 'betting' | 'running' | 'crashed', text: () => string): void {
    this.stageCaption = () => this.stage.setPhase(phase, text());
    this.stageCaption();
  }

  /**
   * Draws the multiplier every animation frame, interpolating between server
   * ticks. This is display only; the server decides what a cash-out pays.
   *
   * It runs on the renderer's ticker rather than its own requestAnimationFrame
   * so that the canvas and the cash-out buttons are updated from one value in
   * one frame. With two independent loops the canvas rendered before the update
   * and the numbers disagreed by a hundredth.
   */
  private startRenderLoop(): void {
    if (this.renderLoopStarted) return;
    this.renderLoopStarted = true;

    this.stage.onFrame(() => {
      // The countdown rides on the same ticker rather than a timer of its own:
      // one clock read per frame, and nothing to drift when the tab is hidden.
      if (this.phase === 'betting') this.countdown.frame(this.curve.serverNow());

      if (this.phase !== 'running') return;
      const x100 = this.curve.currentX100();
      this.stage.setMultiplier(x100);
      this.panels.setMultiplier(x100);
    });
  }

  // ------------------------------------------------------------------------
  // Drawer pages
  // ------------------------------------------------------------------------

  /**
   * The chat button's destination.
   *
   * There is no chat on the server -- no table, no message type, no moderation
   * -- and player-to-player messaging in a real-money product is its own
   * compliance question rather than a UI detail. So the button is real and says
   * so, which is better than an icon that looks broken because it is disabled.
   */
  private showChatPage(): void {
    this.menu.showPage('chat.title', (host) => {
      const p = document.createElement('p');
      p.textContent = t('chat.soon');
      host.replaceChildren(p);
    });
  }

  /**
   * The "···" panel at the end of the history strip.
   *
   * The strip shows what fits; this shows fifty. It reads from the public
   * fairness endpoint rather than from the strip's own chips because the strip
   * only holds what this browser has watched -- a player who just signed in has
   * a handful, and "more" ought to mean more.
   */
  private showHistoryPage(): void {
    this.menu.showPage('history.panelTitle', (host) => {
      const note = document.createElement('p');
      note.textContent = t('history.loading');

      const grid = document.createElement('div');
      grid.className = 'history-grid';
      host.replaceChildren(note, grid);

      void api
        .recentResults(50, this.poolCode)
        .then(({ results }) => {
          note.textContent = results.length ? t('history.panelHint') : t('history.empty');
          grid.replaceChildren(
            ...results.map((r) => {
              const cell = document.createElement('button');
              cell.type = 'button';
              cell.className = band(r.crashX100);

              const mult = document.createElement('span');
              mult.textContent = formatMultiplier(r.crashX100);
              const nonce = document.createElement('span');
              nonce.className = 'n';
              nonce.textContent = `#${r.nonce}`;

              cell.append(mult, nonce);
              cell.title = t('hud.roundTitle', { nonce: r.nonce });
              cell.addEventListener('click', () => this.verifyRound(r.nonce));
              return cell;
            }),
          );
        })
        .catch(() => {
          note.textContent = t('history.failed');
        });
    });
  }

  /**
   * Opens the fairness page for a round.
   *
   * This used to be a `window.alert` holding the same values as one unbroken
   * string, and the verdict in it was the server's own `verified` flag. Both are
   * gone: the page recomputes the result from the seed in this browser, and the
   * hashes are selectable and copyable, which is the entire point of publishing
   * them.
   */
  private verifyRound(nonce: number): void {
    this.fair.show(nonce);
  }

  /** Keeps the fairness page and the strip under the canvas on the same values. */
  private setCommitments(
    nonce: number,
    commitHash: string,
    nextNonce: number,
    nextCommitHash: string,
  ): void {
    this.commitments = { nonce, commitHash, nextNonce, nextCommitHash };
    this.hud.setCommitments(nonce, commitHash, nextNonce, nextCommitHash);
  }

  /** The development-only endpoints are absent in production, so probe for them. */
  private looksLikeDevelopment(): boolean {
    return (
      location.hostname === 'localhost' ||
      location.hostname === '127.0.0.1' ||
      DEV_HOSTS.has(location.hostname)
    );
  }
}

/**
 * Hosts where the development shortcuts may be offered. The server is the real
 * gate -- it does not route those endpoints in production -- so this list only
 * controls whether the buttons are shown.
 */
const DEV_HOSTS = new Set<string>(['figthercrashgame.xyz', 'www.figthercrashgame.xyz']);

/** Turns the snapshot's list into the map the controller keeps. */
function ownBets(list: SnapshotBet[] | undefined, roundId: string): Map<Slot, OwnBet> {
  const out = new Map<Slot, OwnBet>();
  for (const b of list ?? []) {
    const slot = asSlot(b.slot);
    if (slot === null) continue;
    const bet: OwnBet = {
      betId: b.betId,
      roundId,
      amount: b.amount,
      settled: b.settled,
    };
    if (b.autoCashoutX100) bet.autoCashoutX100 = b.autoCashoutX100;
    if (b.cashedOutX100) bet.cashedOutX100 = b.cashedOutX100;
    out.set(slot, bet);
  }
  return out;
}

/**
 * Narrows a slot number off the wire.
 *
 * A slot outside the offered range is a server we do not understand -- a third
 * slot added without a client that draws one -- and the right response is to
 * ignore that message rather than to invent a panel for it.
 */
function asSlot(value: number): Slot | null {
  return SLOTS.find((s) => s === value) ?? null;
}

function must(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`main: #${id} is missing from the page`);
  return el;
}

function select(id: string): HTMLSelectElement {
  const el = document.getElementById(id);
  if (!(el instanceof HTMLSelectElement)) throw new Error(`main: #${id} is not a select`);
  return el;
}

void new App().boot().catch((e: unknown) => {
  console.error(e);
  // The translation table may not have loaded, and t() falls back to English on
  // its own, so this is safe to call from the failure path.
  const p = document.createElement('p');
  p.style.padding = '24px';
  p.style.fontFamily = 'system-ui';
  p.textContent = t('app.loadFailed');
  document.body.replaceChildren(p);
});
